import LaunchIcon from '@mui/icons-material/Launch';
import { Box, TableCell, Typography } from '@mui/material';
import { memo } from 'react';

import { DynamicTooltip } from 'shared/ui/Tooltip';

import { getDefaultAlign } from './getDefaultAlign';

import type {
    ReportWidgetColumnConfig,
    ReportWidgetItem,
} from '../../model/types';

function getCellValue(
    item: ReportWidgetItem,
    col: ReportWidgetColumnConfig,
): number | string {
    if (col.valueKey === 'primaryValue') return item.primaryValue;
    if (col.valueKey === 'label') return item.label;
    return item.secondaryValues?.[col.valueKey] ?? '';
}

function formatCell(
    value: number | string,
    format?: ReportWidgetColumnConfig['format'],
): string {
    if (value === '' || value == null) return '—';
    switch (format) {
        case 'currency': {
            const num =
                typeof value === 'number' ? value : parseFloat(String(value));
            if (Number.isNaN(num)) return String(value);
            return new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(num);
        }

        case 'number':
            return new Intl.NumberFormat().format(
                typeof value === 'number'
                    ? value
                    : parseFloat(String(value)) || 0,
            );

        case 'percent': {
            const num =
                typeof value === 'number' ? value : parseFloat(String(value));
            if (Number.isNaN(num)) return String(value);
            return `${num.toFixed(1)}%`;
        }

        default:
            return String(value);
    }
}

interface ReportWidgetTableCellProps {
    item: ReportWidgetItem;
    column: ReportWidgetColumnConfig;
    showColorSwatchInLabelColumn?: ReportWidgetColumnConfig['valueKey'];
    swatchColor?: string;
    onLabelClick?: () => void;
}

export const ReportWidgetTableCell = memo(
    ({
        item,
        column,
        showColorSwatchInLabelColumn,
        swatchColor,
        onLabelClick,
    }: ReportWidgetTableCellProps) => {
        const value = getCellValue(item, column);
        const formatted = formatCell(value, column.format);
        const cellTooltip = column.getCellTooltip
            ? column.getCellTooltip(item) ?? null
            : null;

        const showSwatch =
            showColorSwatchInLabelColumn === column.valueKey && swatchColor;

        const makeBold = column.bold || column.highlightColor ? true : false;
        const shouldWrap = column.allowWrap === true;
        const lineClamp =
            shouldWrap && column.wrapLines != null && column.wrapLines > 0
                ? column.wrapLines
                : null;
        const textStyles = {
            fontWeight: makeBold ? 500 : undefined,
            minWidth: 0,
            whiteSpace: shouldWrap ? 'normal' : 'nowrap',
            overflowWrap: shouldWrap ? ('anywhere' as const) : undefined,
            wordBreak: shouldWrap ? ('break-word' as const) : undefined,
            ...(lineClamp != null
                ? {
                      display: '-webkit-box',
                      overflow: 'hidden',
                      WebkitLineClamp: lineClamp,
                      WebkitBoxOrient: 'vertical',
                  }
                : {}),
        };

        const baseContent = showSwatch ? (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: swatchColor ?? 'action.hover',
                        flexShrink: 0,
                        marginTop: '0.35em',
                    }}
                />
                <Typography variant="body2" component="span" sx={textStyles}>
                    {formatted}
                </Typography>
            </Box>
        ) : (
            <Typography variant="body2" component="span" sx={textStyles}>
                {formatted}
            </Typography>
        );

        const clickableContent =
            onLabelClick != null ? (
                <Box
                    onClick={(e) => {
                        e.stopPropagation();
                        onLabelClick();
                    }}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: 0,
                        cursor: 'pointer',
                        '& .label-text': {
                            transition:
                                'color 0.15s ease, text-decoration 0.15s ease',
                        },
                        '& .details-icon': {
                            opacity: 0,
                            transition: 'opacity 0.15s ease',
                        },
                        '&:hover .label-text': {
                            textDecoration: 'underline',
                            color: 'text.primary',
                        },
                        '&:hover .details-icon': {
                            opacity: 1,
                        },
                    }}
                >
                    <Box className="label-text" sx={{ minWidth: 0 }}>
                        {baseContent}
                    </Box>
                    <LaunchIcon
                        className="details-icon"
                        aria-label={column.label}
                        sx={{
                            fontSize: 14,
                            verticalAlign: 'middle',
                        }}
                    />
                </Box>
            ) : (
                baseContent
            );

        const cellInner = cellTooltip ? (
            <DynamicTooltip content={cellTooltip}>
                {clickableContent}
            </DynamicTooltip>
        ) : (
            clickableContent
        );

        return (
            <TableCell
                align={getDefaultAlign(column)}
                sx={{
                    fontWeight:
                        column.bold || column.highlightColor ? 500 : undefined,
                    color: column.highlightColor ?? undefined,
                    minWidth:
                        column.minWidth != null
                            ? `${column.minWidth}px`
                            : undefined,
                    maxWidth:
                        column.maxWidth != null
                            ? `${column.maxWidth}px`
                            : undefined,
                    verticalAlign: 'top',
                }}
            >
                {cellInner}
            </TableCell>
        );
    },
);
